import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ASSETS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0A0A]">
      <div className="hidden lg:block relative bg-black border-r border-[#D4AF37]/20">
        <div className="tribal-stripe absolute top-0 left-0 right-0 h-2" />
        <div className="h-full flex flex-col items-center justify-center p-16">
          <img src={ASSETS.seal} alt="Seal" className="h-56 w-56 object-contain" />
          <h2 className="font-serif-display text-4xl text-[#F5F3EF] mt-10 text-center">Welcome Home</h2>
          <p className="text-white/50 text-center mt-4 max-w-sm">
            Return to your place among the people of the Indigenous Black American Nation.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 sm:p-16 bg-[#0A0A0A]">
        <div className="w-full max-w-md">
          <Link to="/" className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">&larr; The Black American Government</Link>
          <h1 className="font-serif-display text-5xl font-medium tracking-tight mt-6 mb-2 text-[#F5F3EF]">Member Login</h1>
          <p className="text-[#A8A29E] mb-8">Access your membership dossier.</p>

          {error && (
            <div className="bg-[#C8102E]/15 border border-[#C8102E]/40 text-[#F5A3A3] px-4 py-3 mb-6 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={submit} className="space-y-6">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-[#A8A29E]">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-none border-0 border-b-2 border-[#F5F3EF]/20 bg-transparent text-[#F5F3EF] focus-visible:ring-0 focus-visible:border-[#D4AF37] px-0"
                data-testid="login-email-input" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-[#A8A29E]">Password</Label>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-none border-0 border-b-2 border-[#F5F3EF]/20 bg-transparent text-[#F5F3EF] focus-visible:ring-0 focus-visible:border-[#D4AF37] px-0"
                data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-none bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C8102E] hover:text-white py-6" data-testid="login-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter"}
            </Button>
          </form>

          <p className="text-sm text-[#A8A29E] mt-8">
            Not yet a member?{" "}
            <Link to="/register" className="text-[#D4AF37] font-semibold hover:underline" data-testid="login-to-register">Declare your membership</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
